import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Message} from "primeng/api";
import {IFlow} from "../../../../../data/interfaces/iflow.interface";
import {ApiFlowDataService, INoElegible} from "../../../../../data/services/api/flow/api-flow-data.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {CurrencyPipe} from "@angular/common";
import {SelectEnumPipe} from "../../../../../shared/pipes";
import {PERIODS_TYPE_ENUM_ES} from "../../../../../data/enum/period.enum";
import {
  faArrowDown,
  faBolt,
  faCalculator,
  faCheck,
  faCircleExclamation,
  faHandHoldingDollar,
  faRotateRight,
  faShieldHalved,
  faTriangleExclamation,
  faUserClock
} from "@fortawesome/free-solid-svg-icons";
import {ESTADOS_LX} from "../../../../../shared/ui/liga-ui";

// Una oferta como la muestra la pantalla. `key` y `name` son los de siempre
// (`key` es el installmentCount que se manda en applyOffer); lo demás es para
// pintar la tarjeta y el resumen.
export interface IOfertaPantalla {
  key: number
  name: string
  cuota: number
  // «12 quincenas»
  plazo: string
  // «por quincena»
  porPeriodo: string
  // Opcionales de calculateOffers (motor del panel): null si no vienen.
  comision: number | null
  dispersar: number | null
  // «Pago más bajo» / «Terminas antes» (solo con 2 o más ofertas).
  etiqueta: string
}

// Lo que puede traer cada oferta. MAS_NOMINA manda installmentAmount como
// texto (toFixed); creationFee y amountToDisperse llegan solo con el motor.
interface IOfertaApi {
  installmentCount: number
  installmentAmount: number | string
  creationFee?: number | string | null
  amountToDisperse?: number | string | null
}

type Etapa = 0 | 1 | 2

// Bloqueos que se resuelven con el tiempo (más antigüedad, cumplir la edad mínima).
const BLOQUEOS_CON_EL_TIEMPO = ['MIN_ANTIGUITY', 'MIN_AGE']

// Periodicidad en minúsculas, en plural y singular, para «12 quincenas» y «por quincena».
const PERIODOS: Record<string, {plural: string; singular: string; adjetivo: string}> = {
  BIWEEKLY_PERIOD: {plural: 'catorcenas', singular: 'catorcena', adjetivo: 'catorcenales'},
  MONTHLY_PERIOD: {plural: 'meses', singular: 'mes', adjetivo: 'mensuales'},
  SEMIMONTHLY_PERIOD: {plural: 'quincenas', singular: 'quincena', adjetivo: 'quincenales'},
  WEEKLY_PERIOD: {plural: 'semanas', singular: 'semana', adjetivo: 'semanales'}
}

const numeroOpcional = (valor: unknown): number | null => {
  if (valor === null || valor === undefined || valor === '') return null
  const n = Number(valor)
  return Number.isFinite(n) ? n : null
}

@Component({
  selector: 'app-offer-component',
  templateUrl: './offer-component.component.html',
  styleUrls: ['./offer-component.component.scss'],
  providers: [CurrencyPipe, SelectEnumPipe]
})
export class OfferComponentComponent implements OnInit{
  responseMessage: Message[] = []
  showMessage = false
  entity!: FormGroup
  min = 0
  max = 0
  offers: IOfertaPantalla[] = []
  period =  ''
  $_periodsEnum = PERIODS_TYPE_ENUM_ES
  @Input() data!: IFlow
  @Input() pushingId: string = ''
  @Output() onEndProcess = new EventEmitter()
  @Output() onUpdateStage = new EventEmitter()
  @ViewChild('seccionOfertas') seccionOfertas?: ElementRef<HTMLElement>
  @ViewChild('alertaAplicar') alertaAplicar?: ElementRef<HTMLElement>
  @ViewChild('estadoNoElegible') estadoNoElegible?: ElementRef<HTMLElement>

  // ---- Solo pantalla (no cambian lo que se manda al API) ----
  cargando = false
  errorCarga = ''
  calculando = false
  aplicando = false
  // Dónde se pinta el r.msg de responseMessage: junto al monto o junto a la oferta.
  errorEn: '' | 'calcular' | 'aplicar' = ''
  // calculateOffers respondió bien pero sin plazos para ese monto.
  sinOfertas = false
  // Monto con el que se calcularon las ofertas que se ven.
  montoCalculado: number | null = null
  // Todas las ofertas traen la misma comisión y el mismo monto a dispersar: se
  // dicen una vez arriba (y en el resumen) en lugar de repetirlos en cada tarjeta.
  extrasIguales = false
  // Barra del monto: alineada a múltiplos de `pasoRango`; los extremos se topan a min/max.
  rangoMin = 0
  rangoMax = 0
  pasoRango = 100
  anuncio = ''
  // Las reglas de crédito de la nómina (antigüedad o edad capturadas) no le
  // permiten pedir un crédito hoy: 422 NO_ELEGIBLE de getOffers, calculateOffers
  // o applyOffer. Reemplaza toda la pantalla de la oferta.
  noElegible: INoElegible | null = null

  readonly estados = ESTADOS_LX
  readonly iconos = {
    oferta: faHandHoldingDollar,
    calcular: faCalculator,
    error: faCircleExclamation,
    aviso: faTriangleExclamation,
    reintentar: faRotateRight,
    seguro: faShieldHalved,
    listo: faCheck,
    pagoBajo: faArrowDown,
    rapido: faBolt,
    noElegible: faUserClock
  }

  constructor(
    private flowDataService: ApiFlowDataService,
    private fb: FormBuilder,
    private currencyPipe: CurrencyPipe,
    private selectedEnumPipe: SelectEnumPipe
  ) {
  }

  ngOnInit() {
    this.getOffers()
  }

  getOffers() {
    this.cargando = true
    this.errorCarga = ''
    this.noElegible = null
    this.flowDataService.getOffers({idDocument: this.data.id, idStage: this.data.userDocumentId, pushingId: this.pushingId}).subscribe({
      next: r => {
        this.cargando = false
        if (r.noElegible) {
          this.mostrarNoElegible(r.noElegible)
        } else if (!r.error) {
          this.data.userDocumentId = r.data.stageId
          this.entity = this.fb.group({
            amount: [0, [Validators.required, Validators.min(r.data.loanProperties.minAmount), Validators.max(r.data.loanProperties.loanAmountAllowed)]],
            installmentCount: ['', [Validators.required]]
          })
          this.min = r.data.loanProperties.minAmount
          this.max = r.data.loanProperties.loanAmountAllowed
          this.period = r.data.loanProperties.period
          this.prepararRango()
          // Cualquier cambio del monto (teclado, barra o atajos) invalida las ofertas que se ven.
          this.entity.get('amount')?.valueChanges.subscribe(valor => {
            if (this.offers.length && Number(valor) !== this.montoCalculado) this.clearOffers()
            else this.limpiarAvisos()
          })
        } else {
          this.errorCarga = r.msg || 'No pudimos cargar tu oferta.'
        }
      },
      error: () => {
        this.cargando = false
        this.errorCarga = 'Hubo un error de conexión.'
      }
    })
  }

  clearOffers() {
    this.offers = []
    this.montoCalculado = null
    // La oferta elegida era para el monto anterior: hay que volver a elegir.
    this.entity?.get('installmentCount')?.setValue('')
    this.limpiarAvisos()
  }

  calculateOffers() {
    if (this.calculando || this.aplicando) return
    this.offers = []
    this.responseMessage = []
    this.errorEn = ''
    this.sinOfertas = false
    this.calculando = true
    this.anuncio = 'Calculando tus opciones de pago…'
    const monto = Number(this.entity.value.amount)
    this.flowDataService.calculateOffers({idDocument: this.data.id, idStage: this.data.userDocumentId, pushingId: this.pushingId, ...this.entity.value}).subscribe({
      next: r => {
        this.calculando = false
        if (r.noElegible) {
          this.mostrarNoElegible(r.noElegible)
        } else if (!r.error) {
          this.offers = this.armarOfertas(r.data.offers ?? [])
          this.extrasIguales = this.offers.length > 1 && this.offers.every(o => o.comision === this.offers[0].comision && o.dispersar === this.offers[0].dispersar)
          this.montoCalculado = monto
          this.sinOfertas = this.offers.length === 0
          this.anuncio = this.sinOfertas
            ? 'No encontramos plazos para este monto.'
            : `Encontramos ${this.offers.length} ${this.offers.length === 1 ? 'opción' : 'opciones'} de pago. Elige una.`
          if (!this.sinOfertas) this.mostrar(() => this.seccionOfertas)
        } else {
          this.responseMessage = [
            {
              summary: r.msg,
              severity: r.error ?  'error': 'success',
              closable: false,
            }
          ]
          this.errorEn = 'calcular'
          this.anuncio = ''
        }
      },
      error: () => this.errorDeConexion('calcular')
    })
  }

  understand() {
    this.onEndProcess.emit(true)
  }

  applyForOffer() {
    if (this.aplicando || this.calculando) return
    this.aplicando = true
    this.responseMessage = []
    this.errorEn = ''
    this.flowDataService.applyOffers({idDocument: this.data.id, idStage: this.data.userDocumentId, pushingId: this.pushingId, amount: this.entity.value.amount, installmentCount: this.entity.value.installmentCount.key}).subscribe({
      next: r => {
        this.aplicando = false
        if (r.noElegible) {
          this.mostrarNoElegible(r.noElegible)
        } else if (!r.error) {
          this.onEndProcess.emit(true)
        } else {
          this.responseMessage = [
            {
              summary: r.msg,
              severity: r.error ?  'error': 'success',
              closable: false,
            }
          ]
          this.errorEn = 'aplicar'
          this.mostrar(() => this.alertaAplicar, 'center')
        }
      },
      error: () => this.errorDeConexion('aplicar')
    })
  }

  // ---------- Pantalla ----------

  // Monto desde la barra: múltiplos de pasoRango, topado a [min, max].
  alDeslizar(event: Event) {
    const valor = Number((event.target as HTMLInputElement).value)
    this.usarMonto(this.topar(valor))
  }

  usarMonto(valor: number) {
    const control = this.entity?.get('amount')
    if (!control) return
    control.setValue(valor)
    control.markAsDirty()
  }

  get montoInvalido(): boolean {
    const control = this.entity?.get('amount')
    return !!control && control.invalid && (control.dirty || control.touched)
  }

  get monto(): number {
    return Number(this.entity?.value?.amount) || 0
  }

  // Valor de la barra (el monto escrito, dentro del rango).
  get valorRango(): number {
    return Math.min(Math.max(this.monto, this.rangoMin), this.rangoMax)
  }

  get pctRango(): number {
    const ancho = this.rangoMax - this.rangoMin
    if (ancho <= 0) return 100
    return Math.round(((this.valorRango - this.rangoMin) / ancho) * 1000) / 10
  }

  get elegida(): IOfertaPantalla | null {
    const valor = this.entity?.get('installmentCount')?.value
    return this.offers.find(o => o === valor) ?? null
  }

  // 0 = monto, 1 = plazo, 2 = aceptar.
  get etapa(): Etapa {
    if (!this.offers.length) return 0
    return this.elegida ? 2 : 1
  }

  claseEtapa(i: number): string {
    if (i < this.etapa) return 'is-listo'
    return i === this.etapa ? 'is-actual' : ''
  }

  get textoCalcular(): string {
    const control = this.entity?.get('amount')
    if (!control || control.valid) return 'Ver mis opciones de pago'
    return this.monto > 0 ? 'Ajusta el monto para continuar' : 'Escribe un monto para continuar'
  }

  get textoAceptar(): string {
    const o = this.elegida
    if (!o) return 'Elige un plazo para continuar'
    return `Aceptar oferta de ${o.key} ${o.key === 1 ? 'pago' : 'pagos'} de ${this.currencyPipe.transform(o.cuota, 'MXN')}`
  }

  // «quincenales», para «Pagos quincenales para $10,000.00».
  get periodoAdjetivo(): string {
    return PERIODOS[this.period]?.adjetivo ?? 'fijos'
  }

  // Enter en el monto: calcula si todavía no hay ofertas y el monto es válido.
  alEnviar() {
    if (!this.offers.length && this.entity?.get('amount')?.valid) this.calculateOffers()
  }

  get mensajeError(): string {
    return String(this.responseMessage[0]?.summary ?? '')
  }

  // Lo que lee el lector de pantalla en cada opción (la tarjeta visible va aria-hidden).
  lecturaOferta(o: IOfertaPantalla): string {
    const partes = [`${o.plazo} de ${this.currencyPipe.transform(o.cuota, 'MXN')} ${o.porPeriodo}`]
    if (!this.extrasIguales && o.dispersar !== null) partes.push(`recibes ${this.currencyPipe.transform(o.dispersar, 'MXN')}`)
    if (!this.extrasIguales && o.comision !== null) partes.push(`comisión ${this.currencyPipe.transform(o.comision, 'MXN')}`)
    if (o.etiqueta) partes.push(o.etiqueta.toLowerCase())
    return partes.join(', ')
  }

  // Los demás requisitos que no se cumplen (el primero ya es el mensaje principal).
  get otrosBloqueos(): string[] {
    const principal = this.noElegible?.mensaje ?? ''
    const mensajes = (this.noElegible?.bloqueos ?? []).map(b => b.mensaje).filter(m => m !== principal)
    return Array.from(new Set(mensajes))
  }

  // «Cuando cumplas el requisito…» solo si se resuelve con el tiempo (no con la edad máxima).
  get noElegiblePorTiempo(): boolean {
    return (this.noElegible?.bloqueos ?? []).some(b => BLOQUEOS_CON_EL_TIEMPO.includes(b.codigo))
  }

  porPlazo(_: number, oferta: IOfertaPantalla) {
    return oferta.key
  }

  private armarOfertas(ofertas: IOfertaApi[]): IOfertaPantalla[] {
    const periodo = PERIODOS[this.period]
    const lista: IOfertaPantalla[] = ofertas.map(o => {
      const cuota = Number(o.installmentAmount)
      const n = Number(o.installmentCount)
      return {
        key: o.installmentCount,
        name: `${this.currencyPipe.transform(o.installmentAmount, 'MXN')} - ${o.installmentCount} ${this.selectedEnumPipe.transform(this.period, this.$_periodsEnum)}`,
        cuota,
        plazo: periodo ? `${n} ${n === 1 ? periodo.singular : periodo.plural}` : `${n} ${n === 1 ? 'pago' : 'pagos'}`,
        porPeriodo: periodo ? `por ${periodo.singular}` : 'por pago',
        comision: numeroOpcional(o.creationFee),
        dispersar: numeroOpcional(o.amountToDisperse),
        etiqueta: ''
      }
    })
    if (lista.length > 1) {
      const masBaja = lista.reduce((a, b) => (b.cuota < a.cuota ? b : a))
      const masCorta = lista.reduce((a, b) => (b.key < a.key ? b : a))
      masBaja.etiqueta = 'Pago más bajo'
      if (masCorta !== masBaja) masCorta.etiqueta = 'Terminas antes'
    }
    return lista
  }

  private prepararRango() {
    const ancho = this.max - this.min
    this.pasoRango = ancho >= 1000 ? 100 : 1
    this.rangoMin = Math.floor(this.min / this.pasoRango) * this.pasoRango
    this.rangoMax = Math.max(this.rangoMin, Math.ceil(this.max / this.pasoRango) * this.pasoRango)
  }

  private topar(valor: number): number {
    if (valor <= this.min) return this.min
    if (valor >= this.max) return this.max
    return Math.min(Math.max(Math.round(valor / this.pasoRango) * this.pasoRango, this.min), this.max)
  }

  private mostrarNoElegible(noElegible: INoElegible) {
    this.noElegible = noElegible
    this.responseMessage = []
    this.errorEn = ''
    this.sinOfertas = false
    this.anuncio = `Por ahora no puedes solicitar un crédito. ${noElegible.mensaje}`
    this.mostrar(() => this.estadoNoElegible)
  }

  private limpiarAvisos() {
    this.sinOfertas = false
    if (this.errorEn) {
      this.responseMessage = []
      this.errorEn = ''
    }
  }

  private errorDeConexion(donde: 'calcular' | 'aplicar') {
    this.calculando = false
    this.aplicando = false
    this.responseMessage = [{summary: 'Hubo un error de conexión. Inténtalo de nuevo.', severity: 'error', closable: false}]
    this.errorEn = donde
  }

  // Lleva a la vista lo que acaba de aparecer (sin animar si el usuario lo pidió).
  private mostrar(elemento: () => ElementRef<HTMLElement> | undefined, bloque: ScrollLogicalPosition = 'start') {
    setTimeout(() => {
      const el = elemento()?.nativeElement
      if (!el || typeof el.scrollIntoView !== 'function') return
      const reducido = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      el.scrollIntoView({behavior: reducido ? 'auto' : 'smooth', block: bloque})
    })
  }
}
